import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Switch,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import SaveIcon from '@mui/icons-material/Save';
import { supabase } from '../config/supabase';
import { useAuditUserId } from '../auth/AuthContext';
import { usePersistentScrollRestoration, usePersistentViewState } from '../hooks/usePersistentViewState';
import {
  advancingDefaultStrategy,
  defaultStrategyForAdvancementType,
  defaultMultiTeamSchedulingConfig,
  normalizeMultiTeamSchedulingConfig,
  nonAdvancingDefaultStrategy,
  validateStrategyProfile,
  type AdvancementType,
  type ChangeSensitivity,
  type DraftAssignmentPolicy,
  type MultiTeamSchedulingConfig,
  type RecommendationPolicy,
  type RecommendationStrategyProfile,
  type StandbyAssignmentPolicy,
  type TentativePlayerPolicy,
  type WarningSeverity,
} from '../utils/lineupRecommendationEngine';

type StrategyRecord = {
  id: string;
  name: string;
  advancement_type: AdvancementType;
  is_system_default: boolean;
  is_active: boolean;
  competitiveness_weight: number;
  participation_fairness_weight: number;
  singles_opportunity_weight: number;
  schedule_spacing_weight: number;
  partner_compatibility_weight: number;
  home_away_balance_weight: number;
  singles_edr_weight: number;
  singles_utr_weight: number;
  doubles_edr_weight: number;
  doubles_utr_weight: number;
  change_sensitivity: ChangeSensitivity;
  tentative_player_policy: TentativePlayerPolicy;
  preferred_pair_bonus?: number;
  preferred_pair_separation_penalty?: number;
  strongly_preferred_pair_bonus?: number;
  strongly_preferred_pair_separation_penalty?: number;
  avoid_pairing_penalty?: number;
  poor_compatibility_penalty?: number;
  good_compatibility_bonus?: number;
  excellent_compatibility_bonus?: number;
  multi_team_scheduling_config?: MultiTeamSchedulingConfig | null;
  enforce_target_matches?: boolean | null;
  target_match_variance?: number | null;
  enforce_max_sit?: boolean | null;
  max_weeks_without_match?: number | null;
  use_partner_compatibility?: boolean | null;
  use_partner_scheduling_preferences?: boolean | null;
  version: number;
};

type AssignedTeamRecord = {
  id: string;
  team_name: string;
};

type CrossTeamPolicyRecord = {
  id: string;
  workspace_id: string;
  name: string;
  scheduling_config?: MultiTeamSchedulingConfig | null;
  is_active: boolean;
};

type CrossTeamPolicyTeamRecord = {
  id: string;
  policy_id: string;
  team_id: string;
};

type PolicyTeamOption = {
  id: string;
  team_name: string;
  workspace_id: string;
};

const policyLabels: Record<RecommendationPolicy, string> = {
  block: 'Block',
  warn: 'Warn',
  allow: 'Allow',
};

const severityLabels: Record<WarningSeverity, string> = {
  info: 'Info',
  warning: 'Warning',
  critical: 'Critical',
};

const severityPenalty: Record<WarningSeverity, number> = {
  info: 8,
  warning: 18,
  critical: 35,
};

function ContextTooltip({ title }: { title: ReactNode }) {
  return (
    <Tooltip title={title} arrow placement="top">
      <HelpOutlineIcon
        fontSize="small"
        color="action"
        tabIndex={0}
        sx={{ ml: 0.75, verticalAlign: 'middle', cursor: 'help' }}
      />
    </Tooltip>
  );
}

const emptyProfile: RecommendationStrategyProfile = {
  ...nonAdvancingDefaultStrategy,
  name: 'Custom Recommendation Strategy',
};

function fromRecord(record: StrategyRecord): RecommendationStrategyProfile {
  return {
    id: record.id,
    name: record.name,
    advancementType: record.advancement_type,
    version: record.version,
    competitivenessWeight: record.competitiveness_weight,
    participationFairnessWeight: record.participation_fairness_weight,
    singlesOpportunityWeight: record.singles_opportunity_weight,
    scheduleSpacingWeight: record.schedule_spacing_weight,
    partnerCompatibilityWeight: record.partner_compatibility_weight,
    homeAwayBalanceWeight: record.home_away_balance_weight,
    singlesEdrWeight: record.singles_edr_weight,
    singlesUtrWeight: record.singles_utr_weight,
    doublesEdrWeight: record.doubles_edr_weight,
    doublesUtrWeight: record.doubles_utr_weight,
    changeSensitivity: record.change_sensitivity,
    tentativePlayerPolicy: record.tentative_player_policy,
    preferredPairBonus: record.preferred_pair_bonus ?? 20,
    preferredPairSeparationPenalty: record.preferred_pair_separation_penalty ?? -10,
    stronglyPreferredPairBonus: record.strongly_preferred_pair_bonus ?? 40,
    stronglyPreferredPairSeparationPenalty: record.strongly_preferred_pair_separation_penalty ?? -30,
    avoidPairingPenalty: record.avoid_pairing_penalty ?? -20,
    poorCompatibilityPenalty: record.poor_compatibility_penalty ?? -10,
    goodCompatibilityBonus: record.good_compatibility_bonus ?? 15,
    excellentCompatibilityBonus: record.excellent_compatibility_bonus ?? 30,
    multiTeamScheduling: normalizeMultiTeamSchedulingConfig(record.multi_team_scheduling_config),
    enforceTargetMatches: record.enforce_target_matches === true,
    targetMatchVariance: (record.target_match_variance === 0 || record.target_match_variance === 2 ? record.target_match_variance : 1),
    enforceMaxSit: record.enforce_max_sit === true,
    maxWeeksWithoutMatch: record.max_weeks_without_match ?? 2,
    usePartnerCompatibility: record.use_partner_compatibility !== false,
    usePartnerSchedulingPreferences: record.use_partner_scheduling_preferences !== false,
  };
}

function toPayload(profile: RecommendationStrategyProfile, auditUserId: string | null) {
  return {
    name: profile.name,
    advancement_type: profile.advancementType,
    competitiveness_weight: profile.competitivenessWeight,
    participation_fairness_weight: profile.participationFairnessWeight,
    singles_opportunity_weight: profile.singlesOpportunityWeight,
    schedule_spacing_weight: profile.scheduleSpacingWeight,
    partner_compatibility_weight: profile.partnerCompatibilityWeight,
    home_away_balance_weight: profile.homeAwayBalanceWeight,
    singles_edr_weight: profile.singlesEdrWeight,
    singles_utr_weight: profile.singlesUtrWeight,
    doubles_edr_weight: profile.doublesEdrWeight,
    doubles_utr_weight: profile.doublesUtrWeight,
    change_sensitivity: profile.changeSensitivity,
    tentative_player_policy: profile.tentativePlayerPolicy,
    preferred_pair_bonus: profile.preferredPairBonus ?? 20,
    preferred_pair_separation_penalty: profile.preferredPairSeparationPenalty ?? -10,
    strongly_preferred_pair_bonus: profile.stronglyPreferredPairBonus ?? 40,
    strongly_preferred_pair_separation_penalty: profile.stronglyPreferredPairSeparationPenalty ?? -30,
    avoid_pairing_penalty: profile.avoidPairingPenalty ?? -20,
    poor_compatibility_penalty: profile.poorCompatibilityPenalty ?? -10,
    good_compatibility_bonus: profile.goodCompatibilityBonus ?? 15,
    excellent_compatibility_bonus: profile.excellentCompatibilityBonus ?? 30,
    multi_team_scheduling_config: normalizeMultiTeamSchedulingConfig(profile.multiTeamScheduling),
    enforce_target_matches: profile.enforceTargetMatches === true,
    target_match_variance: profile.targetMatchVariance ?? 1,
    enforce_max_sit: profile.enforceMaxSit === true,
    max_weeks_without_match: profile.maxWeeksWithoutMatch ?? 2,
    use_partner_compatibility: profile.usePartnerCompatibility !== false,
    use_partner_scheduling_preferences: profile.usePartnerSchedulingPreferences !== false,
    version: profile.version,
    updated_by: auditUserId,
    created_by: auditUserId,
  };
}

export function RecommendationStrategies({ readOnly = false }: { readOnly?: boolean }) {
  const auditUserId = useAuditUserId();
  const {
    state: savedViewState,
    setState: setSavedViewState,
    stateKey: viewStateKey,
    loadedStateKey,
  } = usePersistentViewState({
    userId: auditUserId,
    routeKey: 'recommendation-strategies',
    defaultState: { selectedId: '' },
  });
  const [records, setRecords] = useState<StrategyRecord[]>([]);
  const [policyRecords, setPolicyRecords] = useState<CrossTeamPolicyRecord[]>([]);
  const [policyTeamRows, setPolicyTeamRows] = useState<CrossTeamPolicyTeamRecord[]>([]);
  const [policyTeamOptions, setPolicyTeamOptions] = useState<PolicyTeamOption[]>([]);
  const [selectedPolicyId, setSelectedPolicyId] = useState('');
  const [policyDraftName, setPolicyDraftName] = useState('Fall Competitive Teams');
  const [policyDraftTeamIds, setPolicyDraftTeamIds] = useState<string[]>([]);
  const [selectedId, setSelectedId] = useState(savedViewState.selectedId);
  const [profile, setProfile] = useState<RecommendationStrategyProfile>(emptyProfile);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ severity: 'success' | 'error' | 'info'; text: string } | null>(null);
  const restoredViewStateKey = useRef('');
  const [viewStateReady, setViewStateReady] = useState(false);

  usePersistentScrollRestoration({ userId: auditUserId, routeKey: 'recommendation-strategies', ready: !loading });

  useEffect(() => {
    if (!auditUserId || loadedStateKey !== viewStateKey) {
      setViewStateReady(false);
      return;
    }
    if (restoredViewStateKey.current === viewStateKey) {
      setViewStateReady(true);
      return;
    }
    setSelectedId(savedViewState.selectedId);
    restoredViewStateKey.current = viewStateKey;
    setViewStateReady(true);
  }, [auditUserId, loadedStateKey, savedViewState, viewStateKey]);

  useEffect(() => {
    if (!viewStateReady || restoredViewStateKey.current !== viewStateKey) return;
    setSavedViewState({ selectedId });
  }, [selectedId, setSavedViewState, viewStateKey, viewStateReady]);

  const validation = useMemo(() => validateStrategyProfile(profile), [profile]);
  const multiTeamScheduling = useMemo(() => normalizeMultiTeamSchedulingConfig(profile.multiTeamScheduling), [profile.multiTeamScheduling]);
  const updateProfile = <K extends keyof RecommendationStrategyProfile>(key: K, value: RecommendationStrategyProfile[K]) => {
    setProfile((current) => ({ ...current, [key]: value }));
  };
  const updateSchedulingConfig = (updater: (current: MultiTeamSchedulingConfig) => MultiTeamSchedulingConfig) => {
    setProfile((current) => ({
      ...current,
      multiTeamScheduling: updater(normalizeMultiTeamSchedulingConfig(current.multiTeamScheduling)),
    }));
  };
  const updatePolicyConfig = <K extends keyof Pick<MultiTeamSchedulingConfig, 'overlappingSameDay' | 'nonOverlappingSameDay' | 'consecutiveDay' | 'consecutiveDayAway' | 'consecutiveDaySingles' | 'recovery' | 'rollingWorkload'>>(
    key: K,
    patch: Partial<MultiTeamSchedulingConfig[K]>,
  ) => {
    const normalizedPatch = 'warningSeverity' in patch && patch.warningSeverity
      ? { ...patch, warningPenalty: severityPenalty[patch.warningSeverity as WarningSeverity] }
      : patch;
    updateSchedulingConfig((current) => ({
      ...current,
      [key]: { ...current[key], ...normalizedPatch },
    }));
  };

  const loadProfiles = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('recommendation_strategy_profiles')
      .select('*')
      .is('deleted_at', null)
      .order('is_system_default', { ascending: false })
      .order('name');
    setLoading(false);
    if (error) {
      setMessage({ severity: 'error', text: error.message });
      return;
    }
    const loaded = (data || []) as unknown as StrategyRecord[];
    setRecords(loaded);
    const selected = loaded.find((record) => record.id === selectedId) || loaded.find((record) => record.id === savedViewState.selectedId) || loaded[0];
    if (selected) {
      setSelectedId(selected.id);
      setProfile(fromRecord(selected));
    } else {
      setSelectedId('');
      setProfile(emptyProfile);
    }
  };

  const loadCrossTeamPolicies = async () => {
    const [policiesResult, policyTeamsResult, teamsResult] = await Promise.all([
      supabase.from('cross_team_scheduling_policies').select('id, workspace_id, name, scheduling_config, is_active').is('deleted_at', null).order('name'),
      supabase.from('cross_team_scheduling_policy_teams').select('id, policy_id, team_id').is('deleted_at', null),
      supabase.from('teams').select('id, team_name, workspace_id').eq('is_active', true).is('deleted_at', null).order('team_name'),
    ]);
    if (policiesResult.error || policyTeamsResult.error || teamsResult.error) {
      const errorMessage = policiesResult.error?.message || policyTeamsResult.error?.message || teamsResult.error?.message || 'Unable to load cross-team scheduling policies.';
      if (!/cross_team_scheduling_policies|cross_team_scheduling_policy_teams/i.test(errorMessage)) setMessage({ severity: 'error', text: errorMessage });
      return;
    }
    const policies = (policiesResult.data || []) as unknown as CrossTeamPolicyRecord[];
    const policyTeams = (policyTeamsResult.data || []) as unknown as CrossTeamPolicyTeamRecord[];
    const teams = (teamsResult.data || []) as unknown as PolicyTeamOption[];
    setPolicyRecords(policies);
    setPolicyTeamRows(policyTeams);
    setPolicyTeamOptions(teams);
    const selected = policies.find((item) => item.id === selectedPolicyId) || policies[0];
    if (selected) {
      setSelectedPolicyId(selected.id);
      setPolicyDraftName(selected.name);
      setPolicyDraftTeamIds(policyTeams.filter((row) => row.policy_id === selected.id).map((row) => row.team_id));
      setProfile((current) => ({ ...current, multiTeamScheduling: normalizeMultiTeamSchedulingConfig(selected.scheduling_config) }));
    }
  };

  useEffect(() => {
    void loadCrossTeamPolicies();
  }, []);

  const selectPolicy = (id: string) => {
    const selected = policyRecords.find((record) => record.id === id);
    setSelectedPolicyId(id);
    setPolicyDraftName(selected?.name || 'Fall Competitive Teams');
    setPolicyDraftTeamIds(policyTeamRows.filter((row) => row.policy_id === id).map((row) => row.team_id));
    if (selected) setProfile((current) => ({ ...current, multiTeamScheduling: normalizeMultiTeamSchedulingConfig(selected.scheduling_config) }));
  };

  const createPolicy = () => {
    setSelectedPolicyId('');
    setPolicyDraftName('New Cross-Team Policy');
    setPolicyDraftTeamIds([]);
    setProfile((current) => ({ ...current, multiTeamScheduling: defaultMultiTeamSchedulingConfig }));
  };

  const savePolicy = async () => {
    if (readOnly || policyDraftTeamIds.length === 0 || !policyDraftName.trim()) return;
    const firstTeam = policyTeamOptions.find((team) => team.id === policyDraftTeamIds[0]);
    if (!firstTeam?.workspace_id) {
      setMessage({ severity: 'error', text: 'Select at least one participating team with a workspace.' });
      return;
    }
    setLoading(true);
    const policyPayload = {
      workspace_id: firstTeam.workspace_id,
      name: policyDraftName.trim(),
      scheduling_config: normalizeMultiTeamSchedulingConfig(multiTeamScheduling),
      updated_by: auditUserId,
      created_by: auditUserId,
    };
    const savedPolicyResult = selectedPolicyId
      ? await supabase.from('cross_team_scheduling_policies').update(policyPayload).eq('id', selectedPolicyId).select('id, workspace_id, name, scheduling_config, is_active').single()
      : await supabase.from('cross_team_scheduling_policies').insert(policyPayload).select('id, workspace_id, name, scheduling_config, is_active').single();
    if (savedPolicyResult.error || !savedPolicyResult.data) {
      setLoading(false);
      setMessage({ severity: 'error', text: savedPolicyResult.error?.message || 'Unable to save cross-team policy.' });
      return;
    }
    const policy = savedPolicyResult.data as unknown as CrossTeamPolicyRecord;
    const existingRows = policyTeamRows.filter((row) => row.policy_id === policy.id);
    const selectedTeamIds = new Set(policyDraftTeamIds);
    const rowsToDelete = existingRows.filter((row) => !selectedTeamIds.has(row.team_id));
    const rowsToInsert = policyDraftTeamIds.filter((teamId) => !existingRows.some((row) => row.team_id === teamId));
    if (rowsToDelete.length > 0) {
      const { error } = await supabase.from('cross_team_scheduling_policy_teams').update({ deleted_at: new Date().toISOString(), deleted_by: auditUserId }).in('id', rowsToDelete.map((row) => row.id));
      if (error) {
        setLoading(false);
        setMessage({ severity: 'error', text: error.message });
        return;
      }
    }
    if (rowsToInsert.length > 0) {
      const { error } = await supabase.from('cross_team_scheduling_policy_teams').insert(rowsToInsert.map((teamId) => ({ policy_id: policy.id, team_id: teamId, created_by: auditUserId })));
      if (error) {
        setLoading(false);
        setMessage({ severity: 'error', text: error.message });
        return;
      }
    }
    setLoading(false);
    setSelectedPolicyId(policy.id);
    setMessage({ severity: 'success', text: 'Cross-team scheduling policy saved.' });
    await loadCrossTeamPolicies();
  };

  const selectProfile = (id: string) => {
    setSelectedId(id);
    const selected = records.find((record) => record.id === id);
    setProfile(selected ? fromRecord(selected) : emptyProfile);
  };

  const saveProfile = async () => {
    if (!validation.valid || readOnly) return;
    setLoading(true);
    const nextVersion = selectedId ? profile.version + 1 : 1;
    const payload = toPayload({ ...profile, version: nextVersion }, auditUserId);
    const result = selectedId
      ? await supabase.from('recommendation_strategy_profiles').update(payload).eq('id', selectedId).select('*').single()
      : await supabase.from('recommendation_strategy_profiles').insert(payload).select('*').single();
    setLoading(false);
    if (result.error) {
      setMessage({ severity: 'error', text: result.error.message });
      return;
    }
    setMessage({ severity: 'success', text: 'Recommendation strategy saved.' });
    await loadProfiles();
    const saved = result.data as unknown as StrategyRecord;
    setSelectedId(saved.id);
    setProfile(fromRecord(saved));
  };

  const duplicateProfile = () => {
    setSelectedId('');
    setProfile((current) => ({ ...current, id: undefined, name: `${current.name} Copy`, version: 1 }));
  };

  const createProfile = () => {
    setSelectedId('');
    setProfile({ ...defaultStrategyForAdvancementType('non_advancing'), id: undefined, name: 'New Recommendation Strategy', version: 1 });
    setMessage({ severity: 'info', text: 'New strategy started. Adjust the settings, then save it.' });
  };

  const deleteProfile = async () => {
    if (readOnly || loading || !selectedId) return;
    const selected = records.find((record) => record.id === selectedId);
    if (!selected) return;
    if (selected.is_system_default) {
      setMessage({ severity: 'error', text: 'System default strategies cannot be deleted. Duplicate it first if you need a custom version.' });
      return;
    }

    setLoading(true);
    const { data: assignedTeamsData, error: assignedTeamsError } = await supabase
      .from('teams')
      .select('id, team_name')
      .eq('recommendation_strategy_profile_id', selectedId)
      .is('deleted_at', null)
      .order('team_name');

    if (assignedTeamsError) {
      setLoading(false);
      setMessage({ severity: 'error', text: assignedTeamsError.message });
      return;
    }

    const assignedTeams = (assignedTeamsData || []) as unknown as AssignedTeamRecord[];
    const warning = assignedTeams.length > 0
      ? `Delete "${selected.name}"? It is assigned to ${assignedTeams.length} team${assignedTeams.length === 1 ? '' : 's'} (${assignedTeams.map((team) => team.team_name).join(', ')}). Those teams will be set to no recommendation strategy.`
      : `Delete "${selected.name}"?`;
    if (!window.confirm(warning)) {
      setLoading(false);
      return;
    }

    const now = new Date().toISOString();
    if (assignedTeams.length > 0) {
      const { error: teamError } = await supabase
        .from('teams')
        .update({ recommendation_strategy_profile_id: null, updated_by: auditUserId })
        .eq('recommendation_strategy_profile_id', selectedId);
      if (teamError) {
        setLoading(false);
        setMessage({ severity: 'error', text: teamError.message });
        return;
      }
    }

    const { error: strategyError } = await supabase
      .from('recommendation_strategy_profiles')
      .update({
        is_active: false,
        deleted_at: now,
        deleted_by: auditUserId,
        delete_reason: 'Deleted from Recommendation Strategies screen',
        updated_by: auditUserId,
      })
      .eq('id', selectedId);

    setLoading(false);
    if (strategyError) {
      setMessage({ severity: 'error', text: strategyError.message });
      return;
    }

    setMessage({
      severity: 'success',
      text: assignedTeams.length > 0
        ? `Strategy deleted. ${assignedTeams.length} team${assignedTeams.length === 1 ? '' : 's'} were set to no recommendation strategy.`
        : 'Strategy deleted.',
    });
    await loadProfiles();
  };

  const resetCoreRules = () => {
    setProfile((current) => ({
      ...current,
      enforceTargetMatches: false,
      targetMatchVariance: 1,
      enforceMaxSit: false,
      maxWeeksWithoutMatch: 2,
      usePartnerCompatibility: true,
      usePartnerSchedulingPreferences: true,
    }));
  };

  const resetSchedulingPolicies = () => {
    setProfile((current) => ({ ...current, multiTeamScheduling: defaultMultiTeamSchedulingConfig }));
  };

  return (
    <Box>
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800 }}>Cross-Team Scheduling Policies</Typography>
          <Typography color="text.secondary">Define scheduling rules for players who participate on multiple CourtCaptain teams.</Typography>
        </Box>
      </Stack>
      {message && <Alert severity={message.severity} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }} spacing={1} sx={{ mb: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Cross-Team Scheduling Policies
              <ContextTooltip
                title={(
                  <Box>
                    <Typography variant="caption" sx={{ display: 'block', fontWeight: 700 }}>How these settings work</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>Block means the player is not eligible for that lineup option.</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>Warn keeps the player eligible and surfaces a workload warning.</Typography>
                    <Typography variant="caption" sx={{ display: 'block' }}>Allow records no policy warning or penalty.</Typography>
                  </Box>
                )}
              />
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Cross-team policies are evaluated after the core team recommendation and only for teams that participate in the policy.
            </Typography>
          </Box>
          <Button startIcon={<RestartAltIcon />} onClick={resetSchedulingPolicies}>Reset Policies</Button>
        </Stack>

        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Policy</InputLabel>
              <Select label="Policy" value={selectedPolicyId} onChange={(event) => selectPolicy(event.target.value)}>
                {policyRecords.map((record) => (
                  <MenuItem key={record.id} value={record.id}>{record.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Policy Name"
              value={policyDraftName}
              onChange={(event) => setPolicyDraftName(event.target.value)}
              fullWidth
            />
            <Button startIcon={<AddIcon />} onClick={createPolicy} disabled={readOnly || loading}>New Policy</Button>
          </Stack>
          <FormControl fullWidth>
            <InputLabel>Participating Teams</InputLabel>
            <Select
              multiple
              label="Participating Teams"
              value={policyDraftTeamIds}
              onChange={(event) => {
                const value = event.target.value;
                setPolicyDraftTeamIds(typeof value === 'string' ? value.split(',') : value as string[]);
              }}
              renderValue={(selected) => selected.map((teamId) => policyTeamOptions.find((team) => team.id === teamId)?.team_name || teamId).join(', ')}
            >
              {policyTeamOptions.map((team) => (
                <MenuItem key={team.id} value={team.id}>
                  <Checkbox checked={policyDraftTeamIds.includes(team.id)} />
                  <ListItemText primary={team.team_name} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Stack direction="row" justifyContent="flex-end">
            <Button variant="outlined" startIcon={<SaveIcon />} disabled={readOnly || loading || policyDraftTeamIds.length === 0 || !policyDraftName.trim()} onClick={() => void savePolicy()}>
              Save Cross-Team Policy
            </Button>
          </Stack>
          <Divider />
          {([
            ['overlappingSameDay', 'Overlapping same-day assignments', 'Recommended: Block. Prevents a player from being assigned to overlapping match windows.'],
            ['nonOverlappingSameDay', 'Same-day non-overlapping assignments', 'Recommended: Warn. Allows doubleheaders when feasible but penalizes compressed workload.'],
            ['consecutiveDay', 'Consecutive-day assignments', 'Recommended: Warn. Detects assignments one calendar day before or after this match.'],
            ['consecutiveDayAway', 'Consecutive-day away assignments', 'Recommended: Warn. Adds incremental travel burden when both adjacent-day matches are away.'],
            ['consecutiveDaySingles', 'Consecutive-day singles assignments', 'Recommended: Warn. Penalizes compressed singles workload.'],
          ] as const).map(([key, label, description]) => (
            <Box key={key}>
              <Typography sx={{ fontWeight: 700 }}>{label}</Typography>
              <Typography variant="caption" color="text.secondary">{description}</Typography>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mt: 1 }}>
                <FormControl fullWidth>
                  <InputLabel>Policy</InputLabel>
                  <Select
                    label="Policy"
                    value={multiTeamScheduling[key].policy}
                    onChange={(event) => updatePolicyConfig(key, { policy: event.target.value as RecommendationPolicy })}
                  >
                    {Object.entries(policyLabels).map(([value, text]) => <MenuItem key={value} value={value}>{text}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl fullWidth>
                  <InputLabel>Warning Severity</InputLabel>
                  <Select
                    label="Warning Severity"
                    value={multiTeamScheduling[key].warningSeverity}
                    onChange={(event) => updatePolicyConfig(key, { warningSeverity: event.target.value as WarningSeverity })}
                  >
                    {Object.entries(severityLabels).map(([value, text]) => <MenuItem key={value} value={value}>{text}</MenuItem>)}
                  </Select>
                </FormControl>
                {key === 'consecutiveDaySingles' && (
                  <FormControl fullWidth>
                    <InputLabel>Singles Mode</InputLabel>
                    <Select
                      label="Singles Mode"
                      value={multiTeamScheduling.consecutiveDaySingles.mode}
                      onChange={(event) => updatePolicyConfig('consecutiveDaySingles', { mode: event.target.value as 'both_singles' | 'either_singles' })}
                    >
                      <MenuItem value="both_singles">Both assignments singles</MenuItem>
                      <MenuItem value="either_singles">Either assignment singles</MenuItem>
                    </Select>
                  </FormControl>
                )}
              </Stack>
            </Box>
          ))}

          <Divider />
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Minimum Recovery Policy</InputLabel>
              <Select
                label="Minimum Recovery Policy"
                value={multiTeamScheduling.recovery.policy}
                onChange={(event) => updatePolicyConfig('recovery', { policy: event.target.value as RecommendationPolicy })}
              >
                {Object.entries(policyLabels).map(([value, text]) => <MenuItem key={value} value={value}>{text}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField type="number" label="Minimum Recovery Hours" value={multiTeamScheduling.recovery.minimumHours} onChange={(event) => updatePolicyConfig('recovery', { minimumHours: Number(event.target.value) })} fullWidth />
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Rolling Workload Policy</InputLabel>
              <Select
                label="Rolling Workload Policy"
                value={multiTeamScheduling.rollingWorkload.policy}
                onChange={(event) => updatePolicyConfig('rollingWorkload', { policy: event.target.value as RecommendationPolicy })}
              >
                {Object.entries(policyLabels).map(([value, text]) => <MenuItem key={value} value={value}>{text}</MenuItem>)}
              </Select>
            </FormControl>
            <TextField type="number" label="Max Assignments" value={multiTeamScheduling.rollingWorkload.maxAssignments} onChange={(event) => updatePolicyConfig('rollingWorkload', { maxAssignments: Number(event.target.value) })} fullWidth />
            <TextField type="number" label="Window Days" value={multiTeamScheduling.rollingWorkload.windowDays} onChange={(event) => updatePolicyConfig('rollingWorkload', { windowDays: Number(event.target.value) })} fullWidth />
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <FormControl fullWidth>
              <InputLabel>Draft Assignment Policy</InputLabel>
              <Select
                label="Draft Assignment Policy"
                value={multiTeamScheduling.assignmentSources.draftPolicy}
                onChange={(event) => updateSchedulingConfig((current) => ({
                  ...current,
                  assignmentSources: { ...current.assignmentSources, draftPolicy: event.target.value as DraftAssignmentPolicy },
                }))}
              >
                <MenuItem value="ignore">Ignore</MenuItem>
                <MenuItem value="provisional">Provisional</MenuItem>
                <MenuItem value="committed">Committed</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Standby Assignment Policy</InputLabel>
              <Select
                label="Standby Assignment Policy"
                value={multiTeamScheduling.assignmentSources.standbyPolicy}
                onChange={(event) => updateSchedulingConfig((current) => ({
                  ...current,
                  assignmentSources: { ...current.assignmentSources, standbyPolicy: event.target.value as StandbyAssignmentPolicy },
                }))}
              >
                <MenuItem value="ignore">Ignore</MenuItem>
                <MenuItem value="context_only">Context warning only</MenuItem>
                <MenuItem value="treat_as_assignment">Treat as assignment</MenuItem>
              </Select>
            </FormControl>
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            <TextField
              type="number"
              label="Default Match Duration Minutes"
              value={multiTeamScheduling.timing.defaultMatchDurationMinutes}
              onChange={(event) => updateSchedulingConfig((current) => ({
                ...current,
                timing: { ...current.timing, defaultMatchDurationMinutes: Number(event.target.value) },
              }))}
              fullWidth
            />
            <TextField
              type="number"
              label="Default Travel Buffer Minutes"
              value={multiTeamScheduling.timing.defaultTravelBufferMinutes}
              onChange={(event) => updateSchedulingConfig((current) => ({
                ...current,
                timing: { ...current.timing, defaultTravelBufferMinutes: Number(event.target.value) },
              }))}
              fullWidth
            />
          </Stack>
        </Stack>
      </Paper>

    </Box>
  );
}
